import { Box, Typography, IconButton, Chip, useTheme, alpha } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { ACCENT } from "./accent";

const COLORS = [ACCENT, "#e0a11b", "#2e9d5a", "#e5484d", "#9b59b6"];
const GREEN = "#2e9d5a";

// Metric rows: pick from result.system; `better` drives the best-value highlight
// (null = neutral, no highlight).
const METRICS = [
  { key: "thrust_to_weight", label: "Thrust : Weight", unit: ":1", better: "high" },
  { key: "hover_throttle_pct", label: "Hover throttle", unit: "%", better: "low" },
  { key: "hover_flight_time_min", label: "Hover flight time", unit: "min", better: "high" },
  { key: "specific_thrust_hover_g_per_w", label: "Efficiency", unit: "g/W", better: "high" },
  { key: "max_total_thrust_g", label: "Max thrust", unit: "g", better: "high" },
  { key: "hover_current_a", label: "Hover current", unit: "A", better: "low" },
  { key: "pitch_speed_kmh", label: "Pitch speed", unit: "km/h", better: null },
  { key: "est_temp_max_c", label: "est. Temp @ max", unit: "°C", better: "low" },
];

/**
 * Client-side setup comparison: pinned snapshots vs the live setup. A metrics
 * table with best-value highlighting plus overlaid thrust curves — the
 * "tweak one thing and see what it buys you" loop.
 */
const ComparisonTray = ({ entries, current, onRemove }) => {
  const theme = useTheme();
  if (!entries.length || !current?.sys) return null;

  const cols = [...entries, { id: "current", label: `${current.label} (current)`, sys: current.sys, curve: current.curve }];
  const grid = alpha(theme.palette.divider, 0.5);

  // Best value per metric (across columns with a value).
  const bestFor = (m) => {
    const vals = cols.map((c) => c.sys?.[m.key]).filter((v) => v != null);
    if (!m.better || vals.length < 2) return null;
    return m.better === "high" ? Math.max(...vals) : Math.min(...vals);
  };

  // Overlaid thrust curves — sweeps share the same throttle steps, so merge by index.
  const chartData = (cols[0].curve || []).map((row, i) => {
    const out = { throttle_pct: row.throttle_pct };
    cols.forEach((c) => {
      out[c.id] = c.curve?.[i]?.thrust_g ?? null;
    });
    return out;
  });

  const cellSx = { py: 0.75, px: 1.5, whiteSpace: "nowrap" };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, letterSpacing: 0.4, mb: 1.5 }}>
        COMPARISON
      </Typography>

      <Box sx={{ overflowX: "auto", borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.6)}` }}>
        <Box component="table" sx={{ borderCollapse: "collapse", width: "100%", minWidth: 560 }}>
          <thead>
            <tr>
              <Box component="th" sx={{ ...cellSx, textAlign: "left" }} />
              {cols.map((c, i) => (
                <Box component="th" key={c.id} sx={{ ...cellSx, textAlign: "right" }}>
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                    <Chip
                      size="small"
                      label={c.label}
                      sx={{ backgroundColor: alpha(COLORS[i % COLORS.length], 0.15), color: COLORS[i % COLORS.length], fontWeight: 600, maxWidth: 220 }}
                    />
                    {c.id !== "current" && (
                      <IconButton size="small" onClick={() => onRemove(c.id)} aria-label="Remove from comparison">
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              ))}
            </tr>
          </thead>
          <tbody>
            {METRICS.map((m) => {
              const best = bestFor(m);
              return (
                <Box component="tr" key={m.key} sx={{ borderTop: `1px solid ${alpha(theme.palette.divider, 0.4)}` }}>
                  <Box component="td" sx={{ ...cellSx, color: theme.palette.text.secondary }}>
                    {m.label}
                  </Box>
                  {cols.map((c) => {
                    const v = c.sys?.[m.key];
                    const isBest = best != null && v === best;
                    return (
                      <Box
                        component="td"
                        key={c.id}
                        sx={{ ...cellSx, textAlign: "right", fontWeight: isBest ? 700 : 400,
                          color: isBest ? GREEN : theme.palette.text.primary }}
                      >
                        {v == null ? "—" : `${v} ${m.unit}`}
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </tbody>
        </Box>
      </Box>

      <Box
        sx={{ mt: 2, p: 2, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.08 : 0.6) }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Total thrust vs throttle
        </Typography>
        <Box sx={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 6, right: 8, bottom: 4, left: -8 }}>
              <CartesianGrid stroke={grid} strokeDasharray="3 3" />
              <XAxis dataKey="throttle_pct" type="number" domain={[5, 100]} tickCount={6}
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }} unit="%" stroke={grid} />
              <YAxis tick={{ fontSize: 11, fill: theme.palette.text.secondary }} stroke={grid} width={48} />
              <Tooltip contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {cols.map((c, i) => (
                <Line key={c.id} type="monotone" dataKey={c.id} name={c.label}
                  stroke={COLORS[i % COLORS.length]} dot={false} strokeWidth={2}
                  strokeDasharray={c.id === "current" ? "6 3" : undefined} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Box>
  );
};

export default ComparisonTray;
